import React, { useRef, useState } from "react";
import toast from "react-hot-toast";

import { importTender } from "../../../services/tendersApi";

/* Tender Search results, in the conversation.
 *
 * One list, ranked by fit, that can be narrowed to a fit band with the chips
 * under the header. A row answers three questions at a glance: how well it
 * fits, what it is, and when it is due — the due date alone on the right so a
 * column of dates scans without reading a title. The reasoning (where you fit,
 * what is missing, what could stop you) folds open under the row, with the
 * actions it leads to; the whole tender opens in the side panel on a click.
 */

/* Counted from now, not from when the card was saved. A reopened chat from
 * last week would otherwise say "3 days left" about something that closed. */
export const withLiveDeadline = (t) => ({
  ...t,
  days_remaining: t?.closes_at
    ? Math.floor((new Date(t.closes_at).getTime() - Date.now()) / 86400000)
    : null,
});

/* "Posted today / 3 days ago" — results are ordered newest first within each fit
 * band, and an order nobody can see reads as arbitrary. Counted from now, like
 * the deadline, so a reopened chat does not call last week's notice new. */
const postedAgo = (iso) => {
  if (!iso) return "";
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days < 0) return "";
  if (days === 0) return "Posted today";
  if (days === 1) return "Posted yesterday";
  if (days < 30) return `Posted ${days} days ago`;
  return `Posted ${new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
};

const shortDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : "";

/* The query is the one Sensa ran, OR-joined and quoted — Tender Search reads the
 * same syntax, so the link lands on exactly this result set, not a guess at it.
 * Unclassified tenders are included to match what the chat searched. */
const searchUrl = (query) =>
  `/tender-search?${new URLSearchParams({ q: query, include_unclassified: "1" })}`;

/* SAM.gov publishes some offices in capitals ("DEPT OF THE ARMY"). Long
 * all-caps names read as shouting in a list, so they are set in title case.
 * Left alone: a single word, usually an acronym (DARPA), and anything with a
 * digit, which is an office code ("FA8612 AFLCMC C3BM C3"). */
const SMALL_WORDS = new Set(["of", "the", "and", "for", "on", "in", "to", "at", "by"]);
const tidyName = (name) => {
  const words = name.trim().split(/\s+/);
  if (words.length < 3 || name !== name.toUpperCase() || !/[A-Z]/.test(name) || /\d/.test(name)) {
    return name.trim();
  }
  return words
    .map((w, i) => {
      const lower = w.toLowerCase();
      if (i > 0 && SMALL_WORDS.has(lower.replace(/[^a-z]/g, ""))) return lower;
      if (w.replace(/[^A-Z]/g, "").length <= 2) return w;
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(" ");
};

export const buyerParts = (t) =>
  (t.buyer_path || t.buyer || "")
    .split("›")
    .map((s) => tidyName(s))
    .filter(Boolean);

const Icon = ({ d, size = 14, stroke = 2.2, fill = "none", className = "" }) => (
  <svg aria-hidden="true" width={size} height={size} viewBox="0 0 24 24" fill={fill} stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" className={className}>
    {[].concat(d).map((p) => <path key={p} d={p} />)}
  </svg>
);
const SEARCH = ["M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14z", "M20 20l-3.5-3.5"];
const SLIDERS = ["M4 7h10M18 7h2M4 17h4M12 17h8", "M14 5v4M8 15v4"];
const TICK = "M5 12.5l4.5 4.5L19 7.5";
const CHEVRON_DOWN = "M6 9l6 6 6-6";
const EXTERNAL = "M7 17L17 7M9 7h8v8";
const BUILDING = ["M4 21V5a1 1 0 0 1 1-1h9a1 1 0 0 1 1 1v16", "M15 9h4a1 1 0 0 1 1 1v11", "M3 21h18", "M8 8h3M8 12h3M8 16h3"];
const WARNING = ["M12 4l9 16H3z", "M12 10v4", "M12 17.5v.01"];
const SPARKLE = "M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z";
const PANEL = ["M4 5h16v14H4z", "M14 5v14"];
const SEAL = [
  "M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76z",
  "M9 12l2 2 4-4",
];

/* How well a tender fits, from the reading against Memory. Green is kept for
 * Strong alone so it means something; Possible is deliberately quiet, and
 * drawn half-full so the three read apart without their colours. */
const FIT_TONE = {
  Strong: "border-[#2DAD4E]/25 bg-[#2DAD4E]/10 text-[#1F7D38]",
  Good: "border-[#A04A97]/20 bg-[#A04A97]/[0.08] text-[#8E4187]",
  Possible: "border-[#18101C]/10 bg-[#18101C]/[0.04] text-[#52525B]",
};

const FitMark = ({ band }) =>
  band === "Possible" ? (
    <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="8.5" fill="none" stroke="currentColor" strokeWidth="2.2" />
      <path d="M12 3.5a8.5 8.5 0 0 1 0 17z" fill="currentColor" />
    </svg>
  ) : (
    <Icon d={SEAL} size={12} stroke={2} />
  );

export const FitBadge = ({ band }) =>
  band ? (
    <span
      className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2 py-[2px] text-[11px] font-semibold ${
        FIT_TONE[band] || FIT_TONE.Possible
      }`}
    >
      <FitMark band={band} />
      {band} fit
    </span>
  ) : null;

const BANDS = ["Strong", "Good", "Possible"];

/* A full read ("Is this worth your time?", run on the tender's full page) is THE
 * verdict for that tender, so its row — and the side panel — show it in place
 * of the quick match. */
const READ_TONE = {
  "Worth a look": "border-[#2DAD4E]/25 bg-[#2DAD4E]/10 text-[#1F7D38]",
  "Problems to resolve first": "border-[#F59E0B]/30 bg-[#FEF3C7]/70 text-[#92400E]",
  "Probably not worth the time": "border-[#18101C]/10 bg-[#18101C]/[0.05] text-[#6B6472]",
  "Not enough information yet": "border-[#18101C]/10 bg-[#18101C]/[0.04] text-[#6B6472]",
};

export const ReadBadge = ({ verdict }) => (
  <span
    className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2 py-[2px] text-[11px] font-semibold ${
      READ_TONE[verdict] || READ_TONE["Not enough information yet"]
    }`}
    title="From the full check against your Memory"
  >
    <Icon d={SEAL} size={12} stroke={2} />
    Checked: {verdict}
  </span>
);

/* What the search was narrowed to, said back in the header. */
const filterPhrases = (f = {}) =>
  [
    f.states?.length && `in ${f.states.join(", ")}`,
    f.closing_within_days && `closing within ${f.closing_within_days} days`,
    f.posted_within_days && `posted in the last ${f.posted_within_days} days`,
    f.set_aside_only && "set-asides only",
    f.stage === "open_for_bids" && "open for bids",
    f.stage === "early" && "RFIs and sources sought",
    f.buyer && `from ${f.buyer}`,
  ].filter(Boolean);

/* The deadline column: one large figure, its date under it. Urgency is the
 * figure's colour — red inside a week — so a column of rows shows which
 * deadline to worry about without reading any of them. */
const DUE_TONE = {
  passed: "text-[#9CA3AF]",
  urgent: "text-[#DC2626]",
  soon: "text-[#EA580C]",
  ok: "text-[#18101C]",
  none: "text-[#6B6472]",
};

const Due = ({ t }) => {
  const days = t.days_remaining;
  const tone = days == null ? "none" : days < 0 ? "passed" : days <= 7 ? "urgent" : days <= 14 ? "soon" : "ok";
  const figure = days == null ? "Open" : days < 0 ? "Closed" : days === 0 ? "Today" : `${days} day${days === 1 ? "" : "s"}`;
  return (
    <div className="text-right">
      <p className="text-[10.5px] font-semibold uppercase tracking-[0.08em] text-[#9CA3AF]">Due</p>
      <p className={`mt-0.5 whitespace-nowrap text-[17px] font-semibold leading-tight tracking-[-0.01em] ${DUE_TONE[tone]}`}>
        {figure}
      </p>
      <p className="mt-0.5 whitespace-nowrap text-[11.5px] text-[#9CA3AF]">
        {t.closes_at ? shortDate(t.closes_at) : "No deadline set"}
      </p>
    </div>
  );
};

const ReasonBox = ({ title, tone, items, empty, mark }) => (
  <div className={`rounded-xl border px-3.5 py-3 ${tone.box}`}>
    <p className={`text-[11px] font-semibold uppercase tracking-[0.06em] ${tone.title}`}>{title}</p>
    {items.length ? (
      <ul className="mt-2 space-y-1.5">
        {items.map((item) => (
          <li key={item} className="flex gap-2 text-[13px] leading-snug text-[#3F3F46]">
            <span className={`mt-[2px] flex-none ${tone.mark}`}>{mark}</span>
            <span>{item.charAt(0).toUpperCase() + item.slice(1)}</span>
          </li>
        ))}
      </ul>
    ) : (
      <p className="mt-2 text-[12.5px] leading-snug text-[#77707E]">{empty}</p>
    )}
  </div>
);

const FIT_BOX = {
  box: "border-[#2DAD4E]/20 bg-[#2DAD4E]/[0.05]",
  title: "text-[#1F7D38]",
  mark: "text-[#2DAD4E]",
};
const READ_BOX = {
  box: "border-[#A04A97]/15 bg-[#A04A97]/[0.04]",
  title: "text-[#8E4187]",
  mark: "text-[#A04A97]",
};
const GAP_BOX = {
  box: "border-[#F59E0B]/25 bg-[#FEF3C7]/40",
  title: "text-[#B45309]",
  mark: "text-[#D97706]",
};

const BUTTON =
  "inline-flex items-center gap-1.5 rounded-lg px-3 py-[7px] text-[12.5px] font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97] focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60";
const PRIMARY = `${BUTTON} bg-[#A04A97] text-white hover:bg-[#8E4187]`;
const SECONDARY = `${BUTTON} border border-[#18101C]/[0.12] bg-white text-[#3F3F46] hover:border-[#A04A97]/40 hover:text-[#8E4187]`;

/* The reasoning behind a row, and what it leads to.
 *
 * Cautions lead, in amber, because a fit is never to be read without its
 * catch. A tender that was not read against Memory claims no fit and no gap —
 * it shows what the notice says and the actions alone. */
const RowDetails = ({ t, full, onOpen, onProjectCreated }) => {
  const [importing, setImporting] = useState(false);
  const [imported, setImported] = useState(false);
  // A ref, not the state: two clicks inside one render would both read
  // `importing` as false and start two imports.
  const busyRef = useRef(false);
  const fit = t.fit;
  const cautions = fit?.cautions || [];

  const startProject = async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    setImporting(true);
    try {
      const result = await importTender(t.id);
      toast.success("Project created. Documents are downloading in the background");
      setImported(true);
      onProjectCreated?.({ projectId: result.folder_id, projectName: result.folder_name || t.title });
    } catch (err) {
      toast.error(err?.response?.data?.error || "Could not create the project. Please try again.");
      busyRef.current = false;
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="px-4 pb-4">
      {(cautions.length > 0 || t.set_aside_label) && (
        <div className="mb-3 flex flex-wrap gap-1.5">
          {cautions.map((c) => (
            <span
              key={c}
              className="inline-flex items-start gap-1.5 rounded-lg border border-[#F59E0B]/30 bg-[#FEF3C7]/60 px-2.5 py-1 text-[12px] font-medium leading-snug text-[#92400E]"
            >
              <Icon d={WARNING} size={13} className="mt-[1px] flex-none" />
              {c.charAt(0).toUpperCase() + c.slice(1)}
            </span>
          ))}
          {t.set_aside_label && (
            <span className="inline-flex items-center rounded-lg border border-[#18101C]/[0.08] bg-[#18101C]/[0.03] px-2.5 py-1 text-[12px] font-medium text-[#52525B]">
              {t.set_aside_label}
            </span>
          )}
        </div>
      )}

      {/* What is being bought, in the notice's own words, when the row's own
          line is the fit reading rather than the notice. */}
      {(full || fit?.line) && t.summary && (
        <p className="mb-3 line-clamp-3 text-[12.5px] leading-[1.55] text-[#6B6472]">
          <span className="font-semibold text-[#52525B]">From the notice: </span>
          {t.summary}
        </p>
      )}

      {full ? (
        // The full check's own words: its reasons, and what you would have to
        // produce. Not mixed with the quick match, so there is one answer.
        <div className={`grid gap-2.5 ${full.youd_need?.length ? "sm:grid-cols-2" : ""}`}>
          <ReasonBox
            title="From the full check"
            tone={READ_BOX}
            items={full.reasons || []}
            empty="The check gave no reasons"
            mark={<span className="block h-1.5 w-1.5 translate-y-[5px] rounded-full bg-current" />}
          />
          {!!full.youd_need?.length && (
            <ReasonBox
              title="You'd need"
              tone={GAP_BOX}
              items={full.youd_need}
              empty=""
              mark={<span className="block h-[2px] w-2.5 translate-y-[7px] rounded bg-current" />}
            />
          )}
        </div>
      ) : fit && (
        // Cards saved before gaps were read have none to show, rather than an
        // empty box claiming nothing is missing.
        <div className={`grid gap-2.5 ${fit.gaps ? "sm:grid-cols-2" : ""}`}>
          <ReasonBox
            title="Where you fit"
            tone={FIT_BOX}
            items={fit.fits || fit.reasons || []}
            empty="Matched on the notice's wording only"
            mark={<Icon d={TICK} size={13} stroke={2.8} />}
          />
          {fit.gaps && (
            <ReasonBox
              title="Gaps to cover"
              tone={GAP_BOX}
              items={fit.gaps}
              empty={fit.line ? "Nothing in the scope is missing from your record" : "Not read against your Memory yet. Open it to check your chances."}
              mark={<span className="block h-[2px] w-2.5 translate-y-[7px] rounded bg-current" />}
            />
          )}
        </div>
      )}

      <div className={`flex flex-wrap items-center gap-2 ${fit || t.summary ? "mt-3.5" : ""}`}>
        {t.source_url && (
          <a href={t.source_url} target="_blank" rel="noopener noreferrer" className={PRIMARY}>
            <Icon d={EXTERNAL} size={12} stroke={2.4} />
            Open on {t.source_label || "source"}
          </a>
        )}
        <button
          type="button"
          onClick={startProject}
          disabled={importing || imported}
          className={t.source_url ? SECONDARY : PRIMARY}
        >
          {importing ? (
            <span
              className={`h-3 w-3 animate-spin rounded-full border-2 motion-reduce:animate-none ${
                t.source_url ? "border-[#A04A97]/25 border-t-[#A04A97]" : "border-white/40 border-t-white"
              }`}
            />
          ) : (
            <Icon d={SPARKLE} size={13} stroke={2} />
          )}
          {imported
            ? "Project created"
            : importing
              ? "Creating the project…"
              : t.is_imported ? "Start another project" : "Start a project"}
        </button>
        <button type="button" onClick={onOpen} className={SECONDARY}>
          <Icon d={PANEL} size={13} stroke={2} />
          Details
        </button>
      </div>
    </div>
  );
};

const TenderResultRow = ({ tender, active, defaultOpen, onOpen, onProjectCreated }) => {
  const [open, setOpen] = useState(defaultOpen);
  const t = withLiveDeadline(tender);
  const full = t.fit?.full_read || null;
  const openTender = () => onOpen?.(tender);
  const [agency, ...office] = buyerParts(t);
  const posted = postedAgo(t.published_at);
  // The fit reading's one line says what it is AND why it fits; without one,
  // the notice's own opening says what it is.
  const line = full?.reasons?.[0] || t.fit?.line || t.summary;

  return (
    // Clicks anywhere in a row — another row, the fold, an action — must not
    // read as a click away that closes the side panel.
    <li data-sensa-keep-panel className={`relative transition-colors ${active ? "bg-[#A04A97]/[0.045]" : ""}`}>
      {/* Which one is open beside the chat. A bar, not a border swap, so the
          row's text does not shift when it becomes active. */}
      {active && <span aria-hidden="true" className="absolute inset-y-3 left-0 w-[3px] rounded-r bg-[#A04A97]" />}

      <div className="flex gap-4 px-4 py-3.5">
        <button
          type="button"
          onClick={openTender}
          aria-current={active ? "true" : undefined}
          className="group min-w-0 flex-1 rounded-lg text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97] focus-visible:ring-offset-4"
        >
          <span className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[12px] text-[#77707E]">
            {full ? <ReadBadge verdict={full.verdict} /> : <FitBadge band={t.fit?.band} />}
            {t.notice_type_label && <span>{t.notice_type_label}</span>}
            {t.notice_type_label && posted && <span aria-hidden="true">·</span>}
            {posted && <span>{posted}</span>}
            {/* A colleague's import shows too, so two people do not bid the
                same contract twice. */}
            {t.is_imported && (
              <span className="inline-flex items-center gap-1 font-semibold text-[#16A34A]">
                <Icon d={TICK} size={11} stroke={3} />
                In a project
              </span>
            )}
          </span>

          <span
            className={`mt-1.5 line-clamp-2 text-[15px] font-semibold leading-snug transition-colors ${
              active ? "text-[#8E4187]" : "text-[#18101C] group-hover:text-[#8E4187]"
            }`}
          >
            {t.title}
          </span>

          {agency && (
            <span className="mt-1 flex min-w-0 items-center gap-1.5 text-[12.5px] text-[#77707E]">
              <Icon d={BUILDING} size={13} stroke={1.9} className="flex-none text-[#9CA3AF]" />
              <span className="truncate">
                <span className="font-semibold text-[#3F3F46]">{agency}</span>
                {office.map((o) => (
                  <React.Fragment key={o}>
                    <span aria-hidden="true" className="mx-1 text-[#B8B2BD]">›</span>
                    {o}
                  </React.Fragment>
                ))}
              </span>
            </span>
          )}

          {line && (
            <span className="mt-1.5 line-clamp-2 text-[13px] leading-[1.5] text-[#52525B]">{line}</span>
          )}
        </button>

        <div className="flex w-[104px] flex-none flex-col items-end justify-between gap-2">
          <Due t={t} />
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-label={open ? "Hide the details" : "Show why it fits and what to do next"}
            className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#A04A97]/[0.08] text-[#A04A97] transition-colors hover:bg-[#A04A97]/[0.15] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97]"
          >
            <Icon d={CHEVRON_DOWN} size={15} stroke={2.4} className={`transition-transform motion-reduce:transition-none ${open ? "rotate-180" : ""}`} />
          </button>
        </div>
      </div>

      {open && <RowDetails t={t} full={full} onOpen={openTender} onProjectCreated={onProjectCreated} />}
    </li>
  );
};

/* One line of work: what the company has done in it, then its tenders. The
 * proof leads because it is the honest answer to "can we win this" — a
 * delivered project a buyer can be shown — and a line of work with none says
 * so rather than looking as strong as one with a record. */
const GroupHeader = ({ group, count }) => (
  <div className="flex items-center gap-2 border-b border-[#18101C]/[0.06] bg-[#FAF8FB] px-4 py-2">
    <span className="flex-none text-[11px] font-semibold uppercase tracking-[0.06em] text-[#52525B]">{group.label}</span>
    {group.proof?.length ? (
      <span className="flex min-w-0 items-center gap-1 text-[11.5px] text-[#1F7D38]" title={group.proof.join(", ")}>
        <Icon d={TICK} size={11} stroke={3} className="flex-none" />
        <span className="truncate">Your proof: {group.proof.join(", ")}</span>
      </span>
    ) : (
      <span className="truncate text-[11.5px] text-[#9CA3AF]">No delivered project in Memory yet</span>
    )}
    <span className="ml-auto flex-none rounded-full bg-[#18101C]/[0.05] px-2 py-0.5 text-[11px] font-semibold text-[#6B6472]">
      {count}
    </span>
  </div>
);

const Chip = ({ active, onClick, children, count }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-[5px] text-[12.5px] font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97] focus-visible:ring-offset-1 ${
      active
        ? "border-[#A04A97] bg-[#A04A97] text-white"
        : "border-[#18101C]/[0.12] bg-white text-[#3F3F46] hover:border-[#A04A97]/40"
    }`}
  >
    {children}
    <span className={active ? "text-white/80" : "text-[#9CA3AF]"}>{count}</span>
  </button>
);

/* What the card searched for, folded away behind "Search terms": it is there
 * so a wrong guess about the company can be seen and fixed in Memory, not so
 * every answer opens with a wall of chips. */
const SearchTerms = ({ recommending, query, themes, checked, byIdentifier, codes = [] }) => {
  const terms = recommending
    ? themes
    : query.split(/\s+OR\s+/i).map((s) => s.replace(/^"|"$/g, "").trim()).filter(Boolean);
  return (
    <div className="border-b border-[#18101C]/[0.06] bg-[#FAF8FB] px-4 py-3">
      <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[#6B6472]">
        {recommending ? "Lines of work from your Memory" : "Searched for"}
      </p>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {terms.map((term) => (
          <span key={term} className="rounded-md border border-[#18101C]/[0.08] bg-white px-2 py-0.5 text-[12px] text-[#3F3F46]">
            {term}
          </span>
        ))}
      </div>
      {/* Classification codes that added tenders filed under them, with their
          official titles so a code is never a bare number to the reader. */}
      {codes.length > 0 && (
        <>
          <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.06em] text-[#6B6472]">
            Plus tenders filed under
          </p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {codes.map((c) => (
              <span
                key={c.code}
                title={c.title}
                className="rounded-md border border-[#A04A97]/20 bg-white px-2 py-0.5 text-[12px] text-[#3F3F46]"
              >
                <span className="font-semibold text-[#8E4187]">{c.code}</span>{" "}
                {c.title.charAt(0) + c.title.slice(1).toLowerCase()}
              </span>
            ))}
          </div>
        </>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-[#77707E]">
        {recommending && checked > 0 && <span>{checked} open tenders with documents checked</span>}
        {!byIdentifier && query && (
          <a
            href={searchUrl(query)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 font-semibold text-[#A04A97] hover:text-[#8E4187] focus:outline-none focus-visible:underline"
          >
            Open these in Tender Search
            <Icon d={EXTERNAL} size={11} stroke={2.4} />
          </a>
        )}
      </div>
    </div>
  );
};

const CopilotTenderResultsCard = ({ message, activeTenderId, onOpenTender, onProjectCreated }) => {
  const {
    tenders = [], total = 0, query = "", matchedBy, closedOnly,
    mode, weakOnly, themes = [], checked = 0, groups = [], emptyThemes = [],
    judged, ruledOutCount = 0, filters = {}, codes = [],
  } = message || {};
  const [band, setBand] = useState("All");
  const [showTerms, setShowTerms] = useState(false);
  if (!tenders.length) return null;

  const recommending = mode === "recommend";
  const byIdentifier = matchedBy === "identifier";
  const fitted = tenders.some((t) => t.fit);
  const counts = BANDS.map((b) => [b, tenders.filter((t) => t.fit?.band === b).length]).filter(([, n]) => n);
  const inBand = (t) => band === "All" || t.fit?.band === band;
  const source = tenders[0]?.source_label || "SAM.gov";
  const n = tenders.length;
  const plural = (word) => `${word}${n === 1 ? "" : "s"}`;

  const title = byIdentifier
    ? total === 1 ? "Found that tender" : `${total} tenders match that number`
    : weakOnly
      ? `${n} ${n === 1 ? "closest match" : "closest matches"} to your work`
      : fitted
        ? `${n} ${plural("tender")} ${n === 1 ? "matches" : "match"} your work`
        : `${total} ${closedOnly ? "closed" : "open"} ${total === 1 ? "tender" : "tenders"} found`;

  const subtitle = [
    source,
    // A lookup by link or number is not filtered — the user named that tender.
    !byIdentifier && "with documents",
    ...filterPhrases(filters),
    judged === true && "read against your Memory",
    judged === false && "not yet read against your Memory",
    recommending ? "grouped by line of work" : fitted ? "sorted by fit" : !byIdentifier && "newest first",
  ].filter(Boolean);

  const canShowTerms = recommending ? themes.length > 0 : !!query && !byIdentifier;
  const more = !recommending && total > n;
  // The first row opens with its reasoning showing, so the fold is discovered.
  const firstId = (recommending && groups.length ? groups[0]?.tenders?.[0] : tenders[0])?.id;

  const row = (t) => (
    <TenderResultRow
      key={t.id}
      tender={t}
      active={String(activeTenderId) === String(t.id)}
      defaultOpen={!!t.fit && t.id === firstId}
      onOpen={onOpenTender}
      onProjectCreated={onProjectCreated}
    />
  );

  return (
    <div className="w-full max-w-[760px] overflow-hidden rounded-2xl border border-[#18101C]/[0.09] bg-white shadow-[0_2px_16px_-8px_rgba(24,16,28,0.18)] animate-copilot-rise motion-reduce:animate-none">
      {/* ---- header ---- */}
      <div className="px-4 pb-3 pt-3.5">
        <div className="flex items-start gap-3">
          <span className="flex h-9 w-9 flex-none items-center justify-center rounded-xl bg-[#A04A97]/10 text-[#A04A97]">
            <Icon d={SEARCH} size={16} stroke={2.4} />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[15px] font-semibold leading-snug text-[#18101C]">{title}</p>
            <p className="mt-0.5 text-[12px] text-[#77707E]">{subtitle.join(" · ")}</p>
          </div>
          {canShowTerms && (
            <button
              type="button"
              onClick={() => setShowTerms((v) => !v)}
              aria-expanded={showTerms}
              className={`inline-flex flex-none items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[12px] font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97] ${
                showTerms
                  ? "border-[#A04A97]/40 bg-[#A04A97]/[0.06] text-[#8E4187]"
                  : "border-[#18101C]/[0.12] text-[#3F3F46] hover:border-[#A04A97]/40"
              }`}
            >
              <Icon d={SLIDERS} size={13} stroke={2.2} />
              Search terms
            </button>
          )}
        </div>

        {counts.length > 1 && (
          <div className="mt-3 flex flex-wrap gap-1.5" role="group" aria-label="Filter by fit">
            <Chip active={band === "All"} onClick={() => setBand("All")} count={n}>All</Chip>
            {counts.map(([b, c]) => (
              <Chip key={b} active={band === b} onClick={() => setBand(b)} count={c}>
                {b} fit
              </Chip>
            ))}
          </div>
        )}

        {weakOnly && (
          <p className="mt-3 rounded-lg bg-[#FEF3C7]/70 px-2.5 py-1.5 text-[12px] font-medium text-[#92400E]">
            Nothing is a strong fit in Tender Search right now. These are the closest.
          </p>
        )}
        {closedOnly && (
          <p className="mt-3 rounded-lg bg-[#FEF3C7]/70 px-2.5 py-1.5 text-[12px] font-medium text-[#92400E]">
            Nothing open matched, so these have already closed.
          </p>
        )}
      </div>

      {showTerms && (
        <SearchTerms recommending={recommending} query={query} themes={themes} checked={checked}
          byIdentifier={byIdentifier} codes={codes} />
      )}

      {/* ---- rows ---- */}
      <div className="border-t border-[#18101C]/[0.06]">
        {recommending && groups.length ? (
          groups.map((g) => {
            const shown = (g.tenders || []).filter(inBand);
            return shown.length ? (
              <section key={g.label} className="border-b border-[#18101C]/[0.06] last:border-b-0">
                <GroupHeader group={g} count={shown.length} />
                <ul className="divide-y divide-[#18101C]/[0.06]">{shown.map(row)}</ul>
              </section>
            ) : null;
          })
        ) : (
          <ul className="divide-y divide-[#18101C]/[0.06]">{tenders.filter(inBand).map(row)}</ul>
        )}
        {recommending && !!emptyThemes.length && band === "All" && (
          /* Searched and empty, said once — so "no recruiting tenders" reads
             as an answer, not as a line of work that was forgotten. */
          <p className="border-t border-[#18101C]/[0.06] px-4 py-2.5 text-[12px] leading-snug text-[#77707E]">
            Nothing open right now for {emptyThemes.join(", ")}.
          </p>
        )}
      </div>

      {/* ---- footer ---- */}
      {/* Every tender the keywords matched is one click away in Tender Search:
          the card shows the few that fit, the page shows them all. */}
      <div className="flex items-center gap-2 border-t border-[#18101C]/[0.06] bg-[#18101C]/[0.015] px-4 py-2.5">
        <span className="text-[12px] text-[#77707E]">
          {more ? `Showing ${n} of ${total}. ` : ""}
          {judged && ruledOutCount > 0
            ? `${ruledOutCount} more matched the words but aren't a fit. `
            : ""}
          {recommending ? "Tenders you can't bid on are left out." : "Select a tender to open it beside the chat."}
        </span>
        {!byIdentifier && query && (
          <a
            href={searchUrl(query)}
            target="_blank"
            rel="noopener noreferrer"
            className="ml-auto inline-flex flex-none items-center gap-1 rounded-lg px-2 py-1 text-[12.5px] font-semibold text-[#A04A97] transition-colors hover:bg-[#A04A97]/[0.07] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97]"
          >
            {more || recommending || ruledOutCount > 0 ? "See all in Tender Search" : "Open in Tender Search"}
            <Icon d={EXTERNAL} size={11} stroke={2.4} />
          </a>
        )}
      </div>
    </div>
  );
};

export default CopilotTenderResultsCard;
